import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

export const AssignmentSection = ({ formData, handleChange, filteredDoctors, nurses, referees }) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold font-[Manrope]">Doctor & Staff Assignment</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="specialty">Specialty/Department</Label>
          <Select value={formData.specialty} onValueChange={(v) => handleChange('specialty', v)}>
            <SelectTrigger data-testid="select-specialty">
              <SelectValue placeholder="Select specialty" />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={5}>
              <SelectItem value="General Medicine">General Medicine</SelectItem>
              <SelectItem value="Cardiology">Cardiology</SelectItem>
              <SelectItem value="Orthopedics">Orthopedics</SelectItem>
              <SelectItem value="Pediatrics">Pediatrics</SelectItem>
              <SelectItem value="Gynecology">Gynecology</SelectItem>
              <SelectItem value="ENT">ENT</SelectItem>
              <SelectItem value="Dermatology">Dermatology</SelectItem>
              <SelectItem value="Neurology">Neurology</SelectItem>
              <SelectItem value="Surgery">Surgery</SelectItem>
              <SelectItem value="Emergency">Emergency</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="assigned_doctor">Assigned Doctor</Label>
          <Select value={formData.assigned_doctor_id} onValueChange={(v) => handleChange('assigned_doctor_id', v)}>
            <SelectTrigger data-testid="select-doctor">
              <SelectValue placeholder="Select doctor" />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={5}>
              {filteredDoctors.map(doc => (
                <SelectItem key={doc.id} value={doc.id}>
                  Dr. {doc.full_name} {doc.department ? `- ${doc.department}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {formData.patient_type === 'indoor' && (
          <div>
            <Label htmlFor="assigned_nurse">Assigned Nurse (Indoor)</Label>
            <Select value={formData.assigned_nurse_id} onValueChange={(v) => handleChange('assigned_nurse_id', v)}>
              <SelectTrigger data-testid="select-nurse">
                <SelectValue placeholder="Select nurse" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                {nurses.map(nurse => (
                  <SelectItem key={nurse.id} value={nurse.id}>
                    {nurse.full_name} {nurse.department ? `- ${nurse.department}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Referral & Payment */}
        <div className="md:col-span-2 grid grid-cols-2 gap-4 mt-4 p-4 bg-slate-50 rounded-lg">
          <div>
            <Label htmlFor="referred_by">Referred By (Optional)</Label>
            <Select value={formData.referred_by_code} onValueChange={(v) => handleChange('referred_by_code', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select referee or skip" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                <SelectItem value="to_be_selected">To Be Selected Later</SelectItem>
                {referees.map(ref => (
                  <SelectItem key={ref.id} value={ref.referee_code}>
                    {ref.name} ({ref.referee_code}) - {ref.commission_percentage}%
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="payment_mode">Payment Mode *</Label>
            <Select value={formData.payment_mode} onValueChange={(v) => handleChange('payment_mode', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={5}>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="cash_plus_insurance">Cash + Insurance</SelectItem>
                <SelectItem value="cashless_insurance">Cashless Insurance</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Waive Consultation Charges */}
          <div className="md:col-span-2 space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="consultation_waived"
                checked={formData.consultation_waived}
                onCheckedChange={(checked) => handleChange('consultation_waived', checked)}
                data-testid="consultation-waived-checkbox"
              />
              <Label htmlFor="consultation_waived" className="font-medium cursor-pointer">
                Waive Consultation Charges
              </Label>
            </div>

            {formData.consultation_waived && (
              <div>
                <Label htmlFor="waive_reason">Reason for Waiving Charges *</Label>
                <Textarea
                  id="waive_reason"
                  value={formData.waive_reason}
                  onChange={(e) => handleChange('waive_reason', e.target.value)}
                  placeholder="e.g., Financial hardship, Staff family member, Emergency case, etc."
                  rows={2}
                  required
                  className="mt-1"
                />
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> {formData.patient_type === 'indoor' 
              ? 'For indoor patients, the assigned nurse will complete further admission formalities once patient reports.' 
              : 'For outdoor patients, the assigned doctor will see this patient in their dashboard.'}
          </p>
        </div>
      </CardContent>
    </Card>

);
